import { Box, Carousel, ResponsiveSection, Stack, type CloudinaryImageProps, type CarouselProps, useScreenSize } from '@stitch-fix/mode-react';
import classNames from 'classnames';
import { useRef } from 'react';
import type { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { MODE_MIN_WIDTH } from '../../utils/values/mediaQueries';
import { type CloudinaryImageFields, CloudinaryImageWrapper } from '../CloudinaryImageWrapper';
import { CloudinaryVideo } from '../CloudinaryVideo/CloudinaryVideo';
import { SizeConfig } from '../CSCloudinaryAsset/utils';
import { JsonRteRenderer } from '../JsonRteRenderer';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
import { ModuleWrapper, type ModuleWrapperProps } from '../ModuleWrapper';
import { useDomCarouselIndex } from './mobileDotNavHelpers';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
import styles from './styles.module.scss';
type MobileNextSlidePreview = 'standard' | 'narrow' | 'none';
const getWidth = (width: string, mobileNextSlidePreview: MobileNextSlidePreview): CarouselProps['slideWidth'] => {
  let mobileWidth;
  if (mobileNextSlidePreview === 'none') {
    mobileWidth = '100%';
  }
  if (mobileNextSlidePreview === 'narrow') {
    mobileWidth = '87%';
  }
  switch (width) {
    case 'large':
      return {
        sm: mobileWidth || '80%',
        md: '384px',
        lg: '384px',
        xl: '496px',
        xxl: '496px'
      };
    case 'medium':
      return {
        sm: mobileWidth || '60%',
        md: '272px',
        lg: '272px',
        xl: '350px',
        xxl: '350px'
      };
    case 'small':
    default:
      return {
        sm: mobileWidth || '40%',
        md: '224px',
        lg: '224px',
        xl: '280px',
        xxl: '280px'
      };
  }
};
const getImageSources = (width: string): CloudinaryImageProps['sizes'] => {
  switch (width) {
    case 'large':
      return {
        sm: 435,
        md: 530,
        lg: 388,
        xl: 399
      };
    case 'medium':
      return {
        sm: 326,
        md: 354,
        lg: 277,
        xl: 342
      };
    case 'small':
    default:
      return {
        sm: 218,
        md: 222,
        xl: 274
      };
  }
};
type DisplayCarouselBackgroundColor = 'blue-45' | 'mint-60' | 'white' | 'forest-60' | 'grapefruit-60' | 'citrus-60';
export type SlideTextColor = 'blue-45' | 'mint-60' | 'white' | 'forest-60' | 'grapefruit-60' | 'citrus-60';
type Slide = {
  content: {
    textColor: SlideTextColor;
    text: ContentStackJSONRichText;
    placement: 'above' | 'below';
  };
  image: CloudinaryImageFields;
};
export type DisplayCarouselProps = {
  backgroundColor?: DisplayCarouselBackgroundColor;
  mobileNavStyle: 'standard' | 'dots';
  mobileNextSlidePreview?: MobileNextSlidePreview;
  roundedCornersOnImages: boolean;
  slideSpacing: 'standard' | 'wide';
  slideWidth: 'small' | 'medium' | 'large';
  slides: Slide[];
  enableOfferOverride?: boolean;
} & Omit<ModuleWrapperProps, 'children'>;
const sizes = new Map<DisplayCarouselProps['slideWidth'], SizeConfig[]>([['small', [{
  minWidth: 0,
  fluidWidth: 35
}, {
  minWidth: MODE_MIN_WIDTH.md,
  staticWidth: 250
}]], ['medium', [{
  minWidth: 0,
  fluidWidth: 55
}, {
  minWidth: MODE_MIN_WIDTH.md,
  staticWidth: 350
}]], ['large', [{
  minWidth: 0,
  fluidWidth: 75
}, {
  minWidth: MODE_MIN_WIDTH.md,
  staticWidth: 530
}, {
  minWidth: MODE_MIN_WIDTH.md,
  staticWidth: 390
}]]]);
export const DisplayCarousel = ({
  backgroundColor = 'white',
  mobileNavStyle,
  mobileNextSlidePreview = 'standard',
  moduleTitle,
  moduleFooter,
  majorTitleTextColor,
  minorTitleTextColor,
  textColor,
  roundedCornersOnImages,
  slides,
  slideWidth,
  slideSpacing,
  alignment,
  enableOfferOverride,
  verticalSpacing
}: DisplayCarouselProps & ModularComponentProps) => {
  const screenSize = useScreenSize();
  const showMobileDotNav = screenSize === 'sm' && mobileNavStyle === 'dots' && mobileNextSlidePreview === 'none';
  const containerRef = useRef<HTMLDivElement>(null);
  const activeSlideIndex = useDomCarouselIndex({
    containerRef
  });
  const activeDotClass = () => {
    switch (backgroundColor) {
      case 'blue-45':
        return styles.activeCitrus;
      default:
        return styles.active;
    }
  };
  const activeDotStyles = activeDotClass();
  return <Box bgColor={backgroundColor} mt={verticalSpacing}>
      <ResponsiveSection width="xxlarge">
        <ModuleWrapper alignment={alignment} moduleTitle={moduleTitle} moduleFooter={moduleFooter} majorTitleTextColor={majorTitleTextColor} minorTitleTextColor={minorTitleTextColor} textColor={textColor} includeYPadding={backgroundColor !== 'white'}>
          <Box ref={containerRef} className={classNames(styles.carouselContainer, {
          [styles.leftAlign]: alignment === 'left',
          [styles.centerAlign]: alignment === 'center',
          [styles.mobileFullBleed]: screenSize === 'sm' && mobileNextSlidePreview === 'none',
          [styles.mobileDotNav]: showMobileDotNav,
          [styles.wideSpacing]: !showMobileDotNav && slideSpacing === 'wide'
        })}>
            <Carousel slideWidth={getWidth(slideWidth, mobileNextSlidePreview)} ariaLabel="Carousel">
              {slides.map((slide, index) => {
              const jsonRteRenderer = !isEmpty(slide.content.text?.children) ? <Box
              // List is static and won't change once rendered
              // eslint-disable-next-line react/no-array-index-key
              key={index} px={0.5}>
                    <JsonRteRenderer data={slide.content.text} options={{
                  color: slide.content.textColor,
                  textProps: {
                    p: {
                      spacingBottom: false
                    }
                  }
                }} enableOfferOverride={enableOfferOverride} />
                  </Box> : null;
              let media = null;
              if (slide.image.src.includes('.mp4')) {
                media = <CloudinaryVideo key={slide.image.src} videoUrl={slide.image.src} videoLabel={slide.image.alt} />;
              } else if (slide.image.src.length > 0) {
                media = <CloudinaryImageWrapper key={slide.image.src} {...slide.image} sources={getImageSources(slideWidth)} cloudinaryAsset={slide.image.cloudinaryAsset} sizes={sizes.get(slideWidth)} imageProps={{
                  loading: 'lazy'
                }} />;
              }
              const renderedMedia = media ? <Box className={classNames(styles.mediaContainer, {
                [styles.roundedCorners]: roundedCornersOnImages,
                [styles.dotNavPlaceholder]: showMobileDotNav
              })}
              // List is static and won't change once rendered
              // eslint-disable-next-line react/no-array-index-key
              key={index}>
                    {media}
                  </Box> : null;
              return (
                // List is static and won't change once rendered
                // eslint-disable-next-line react/no-array-index-key
                <Stack spacing={1} key={index}>
                    {slide.content.placement === 'above' ? [jsonRteRenderer, renderedMedia] : [renderedMedia, jsonRteRenderer]}
                  </Stack>
              );
            })}
            </Carousel>
            {showMobileDotNav && <Box className={styles.mobileDotNavContainer} aria-hidden="true" role="presentation" data-testid="mobile-dot-nav">
                {Array.from({
              length: slides.length
            }).map((_, index) => <Box as="span"
            // eslint-disable-next-line react/no-array-index-key
            key={`dot-nav-${index}`} className={classNames(styles.dot, {
              [activeDotStyles]: index === activeSlideIndex
            })} />)}
              </Box>}
          </Box>
        </ModuleWrapper>
      </ResponsiveSection>
    </Box>;
};